import { useNavigate, useParams } from 'react-router-dom'
import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { Box, useTheme } from '@mui/material'
import type { GridSortModel } from '@mui/x-data-grid-premium'
import PageHeaderWithIcons from '@/components/heder/PageHeaderWithIcons'
import { useAppTheme } from '@/theme/ThemeContext'
import WaterTreesCountCard from '@/components/Analytics/WaterTreesCountCard'
import WaterTreeEditAction from '@/components/Analytics/WaterTreeEditAction'
import WaterTreeEditorDialog from '@/components/Analytics/WaterTreeEditorDialog'
import YearComparisonChart from '@/components/charts/YearComparisonChart'
import DataGridPremiumDemo from '@/components/Table/DataGrid'
import buildColumnsConfig from '@/components/features/config/columns/analytics/analytics.main.column.config'
import EditIcon from '@assets/Analytics/Pen.svg'
import {
  getWaterTreeChartStats,
  getWaterTreeZones,
  getWaterTreeZonesList,
  getWaterTreeZoneTypes,
  createWaterTree,
  updateWaterTree,
  deleteWaterTree,
  WaterMeterRow,
  WaterTreeZone,
  WaterTreeZoneType,
} from '@/services/dashboardService'
import { transformWaterToChartPoints } from '@/utils/chartUtils'
import { useScopedApiFilters } from '@/store/table/table'
import { getTablePageSize } from '@/hooks/useTablePageSize'
import { useSearch } from '@/hooks/useSearch'
import { exportToExcel } from '@/utils/exportUtils'
import { normalizeLossPercent } from '@/utils/analyticsFormat'
import { getWaterTreesSummaryCount } from '@/utils/analyticsCount'

const getZoneKind = (zoneType: number | string | undefined): 1 | 2 => {
  if (Number(zoneType) === 1 || zoneType === 'עץ') return 1
  return 2
}

// The Gateway matches the text against the tree name, the supplier's meter and billing id,
// and the consumer meters of the tree.
const searchResource = (query: string) =>
  query ? [{ name: 'search', operator: 'CONTAINS', value: [query] }] : []

export default function Analytics() {
  const theme = useTheme()
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'
  const apiFilters = useScopedApiFilters('trees')
  const navigate = useNavigate()
  const { siteId } = useParams()

  const [chartRows, setChartRows] = useState<WaterMeterRow[]>([])
  const [chartLoading, setChartLoading] = useState(true)
  const [zones, setZones] = useState<WaterTreeZone[]>([])
  const [totalZones, setTotalZones] = useState(0)
  const [unfilteredTotalZones, setUnfilteredTotalZones] = useState(0)
  // refreshTable reads the site-wide total through this ref. Its own first
  // response sets the total, and a state dependency would recreate the
  // callback and send the same page request a second time.
  const siteTotalRef = useRef(0)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(() => getTablePageSize(50))
  const [sortBy, setSortBy] = useState('zoneName')
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC')
  const [copyOptions, setCopyOptions] = useState<{ id: number; name: string }[]>([])
  const [zoneTypes, setZoneTypes] = useState<WaterTreeZoneType[]>([])
  const [isEditorOpen, setIsEditorOpen] = useState(false)
  const [selectedZone, setSelectedZone] = useState<WaterTreeZone | null>(null)
  const search = useSearch({ onQueryChange: () => setPage(0) })
  const { run: runSearch } = search
  const debouncedSearch = search.query
  const zonesLoading = search.isSearching

  const accountIdRef = useRef(localStorage.getItem('accountId') ?? '')
  const accountId = accountIdRef.current

  const refreshTable = useCallback((siteTotalChanged = false) => {
    if (!accountId) return

    const filters = [...apiFilters, ...searchResource(debouncedSearch)]
    // This card is site-wide and must not use the filtered table total. The ref holds it
    // once any response has set it, so a search alone does not ask for it again.
    const needsSiteTotal =
      siteTotalChanged ||
      apiFilters.length > 0 ||
      (debouncedSearch !== '' && siteTotalRef.current === 0)

    runSearch(
      signal =>
        Promise.all([
          getWaterTreeZones(
            { accountId, page: page + 1, size: pageSize, sortBy, sortOrder },
            filters,
            signal
          ),
          needsSiteTotal
            ? getWaterTreeZones(
                { accountId, page: 1, size: 1, sortBy, sortOrder },
                [],
                signal
              )
            : Promise.resolve(null),
        ]),
      ([{ rows, total }, unfilteredResult]) => {
        setZones(rows)
        setTotalZones(total)
        const siteTotal =
          unfilteredResult?.total ??
          (debouncedSearch ? siteTotalRef.current : total)
        siteTotalRef.current = siteTotal
        setUnfilteredTotalZones(siteTotal)
      },
      err => {
        console.error('[Analytics] zones fetch:', err)
        setZones([])
        setTotalZones(0)
      }
    )
  }, [accountId, apiFilters, debouncedSearch, page, pageSize, runSearch, sortBy, sortOrder])

  useEffect(() => {
    refreshTable()
  }, [refreshTable])

  useEffect(() => {
    setPage(0)
  }, [apiFilters])

  useEffect(() => {
    if (!accountId) return
    getWaterTreeZonesList(accountId)
      .then(setCopyOptions)
      .catch(err => console.error('[Analytics] zones list fetch:', err))
  }, [accountId])

  useEffect(() => {
    if (!accountId) return

    setChartLoading(true)
    getWaterTreeChartStats(accountId)
      .then(rows => { setChartRows(rows); setChartLoading(false) })
      .catch(err => { console.error('[Analytics] chart fetch:', err); setChartLoading(false) })

    getWaterTreeZoneTypes(accountId)
      .then(setZoneTypes)
      .catch(err => console.error('[Analytics] zone-types fetch:', err))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const sortModel = useMemo<GridSortModel>(() => [
    { field: sortBy, sort: sortOrder.toLowerCase() as 'asc' | 'desc' },
  ], [sortBy, sortOrder])

  const handlePaginationChange = (newPage: number, newPageSize: number) => {
    setPage(newPageSize !== pageSize ? 0 : newPage)
    setPageSize(newPageSize)
  }

  const handleSortChange = (field: string, order: 'ASC' | 'DESC') => {
    setPage(0)
    setSortBy(field)
    setSortOrder(order)
  }

  const openCreate = () => {
    setSelectedZone(null)
    setIsEditorOpen(true)
  }

  // Pencil action: the ONLY row path into the edit dialog (row clicks no
  // longer open it). Memoized so the grid's column store effect doesn't
  // re-fire every render; the callback only touches stable setState fns.
  const columnsConfig = useMemo(
    () =>
      buildColumnsConfig((zone: WaterTreeZone) => {
        setSelectedZone(zone)
        setIsEditorOpen(true)
      }),
    []
  )

  // Single click: select only (grid selection provides the highlight).
  const handleRowClick = (zone: WaterTreeZone) => {
    setSelectedZone(zone)
  }

  // Double click: open the row's details page on the route matching its id
  // namespace — hierarchy rows (zoneType 1) carry a root premise id → tree/…,
  // group rows (zoneType 2) carry a group id → group/…. The route split
  // (plan 014) replaced 013's group no-op: both kinds navigate.
  const handleRowDoubleClick = (zone: WaterTreeZone) => {
    const kindSegment = zone.zoneType === 2 ? 'group' : 'tree'
    navigate(`/${siteId}/analytics/${kindSegment}/${zone.id}/details`)
  }
  const handleCreate = async (data: {
    name: string
    zoneTypeId: number | null
    copyFromId?: number | null
  }) => {
    try {
      await createWaterTree(accountId, data)
      setIsEditorOpen(false)
    } catch (err: any) {
      console.error('[Analytics] create failed:', err.response?.data ?? err)
    } finally {
      refreshTable(true)
    }
  }

  const handleUpdate = async (data: {
    name: string
    zoneTypeId: number | null
  }) => {
    if (!selectedZone) return
    try {
      await updateWaterTree(
        accountId,
        selectedZone.id,
        data,
        getZoneKind(selectedZone.zoneType)
      )
      setIsEditorOpen(false)
    } catch (err: any) {
      console.error('[Analytics] update failed:', err.response?.data ?? err)
    } finally {
      refreshTable()
    }
  }

  const handleDelete = async () => {
    if (!selectedZone) return
    try {
      await deleteWaterTree(
        accountId,
        selectedZone.id,
        getZoneKind(selectedZone.zoneType)
      )
    } catch (err: any) {
      console.error('[Analytics] delete failed:', err.response?.data ?? err)
    } finally {
      refreshTable(true)
    }
  }

  const handleExcelExport = useCallback(async () => {
    if (!accountId || totalZones === 0) return

    try {
      const exportRows = (await getWaterTreeZones({
        accountId,
        page: 1,
        size: Math.max(totalZones, pageSize),
        sortBy,
        sortOrder,
      }, [...apiFilters, ...searchResource(debouncedSearch)])).rows

      const exportColumns = columnsConfig
        .filter(column => column.field && column.headerName)
        .map(column => ({
          field: column.field,
          headerName: String(column.headerName),
        }))

      exportToExcel(
        exportColumns,
        exportRows.map(row => ({
          ...row,
          zoneType: row.zoneType === 1 || row.zoneType === '1' ? 'עץ' : row.zoneType === 2 || row.zoneType === '2' ? 'קבוצה' : row.zoneType ?? '-',
          lossPercent: normalizeLossPercent(row.lossPercent),
        })),
        'water-trees.xlsx'
      )
    } catch (err) {
      console.error('[Analytics] Excel export failed:', err)
    }
  }, [accountId, apiFilters, debouncedSearch, pageSize, sortBy, sortOrder, totalZones])

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      bgcolor: theme.palette.background.default,
      direction: 'rtl',
    }}>
      <PageHeaderWithIcons
        title="עצי מים"
        showSearch
        placeholder="חיפוש לפי שם עץ, מספר מד או כרטיס מים"
        onSearchChange={search.setText}
        executeSearch={search.searchNow}
        onExcelExport={handleExcelExport}
      />

      <Box sx={{ flex: 1, minHeight: 0, p: { xs: 1.5, md: 'clamp(12px, 1.2vw, 24px) clamp(18px, 2vw, 40px)' }, display: 'flex', flexDirection: 'column', gap: 0 }}>

        <Box sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-start',
          alignItems: 'flex-end',
          width: '100%',
          mb: 1.5,
          gap: 2.5,
        }}>
          <Box sx={{ flex: 1, minWidth: 0, width: '64%', maxWidth: 1100 }}>
            <YearComparisonChart
              data={transformWaterToChartPoints(chartRows)}
              headerTitle="סך הכל סופק מול נצרך"
              showCompareLabel={false}
              defaultCompare={true}
              height={260}
              showFooter={false}
              loading={chartLoading}
            />
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'clamp(10px, 1vw, 18px)', width: 'clamp(220px, 22vw, 320px)', minWidth: 0, mb: 0.5 }}>
            <WaterTreesCountCard
              value={zonesLoading && unfilteredTotalZones === 0
                ? '...'
                : getWaterTreesSummaryCount(unfilteredTotalZones, totalZones)}
              title="כמות עצי המים באתר"
              width="100%"
              height={110}
              bgcolor={isDarkMode ? '#003089' : '#E3F5FF'}
              sx={{ border: isDarkMode ? '1.5px solid #1D325A' : 'none' }}
            />
            <WaterTreeEditAction
              label="עריכת ויצירת עץ מים"
              iconSrc={EditIcon}
              width="100%"
              height={52}
              onClick={openCreate}
              sx={{ fontWeight: 900, borderRadius: '8px' }}
            />
          </Box>
        </Box>

        <Box sx={{
          flex: 1,
          minHeight: 0,
          border: isDarkMode ? '1.5px solid #1D325A' : '1px solid #EAEDF2',
          borderRadius: 0,
          overflow: 'hidden',
          bgcolor: isDarkMode ? '#091327' : '#FFFFFF',
        }}>
          <DataGridPremiumDemo
            columns={columnsConfig}
            rows={zones}
            hideFooter={true}
            headerPosition="bottom"
            pageSize={pageSize}
            serverSideRowCount={totalZones}
            serverSidePage={page}
            onPaginationChange={handlePaginationChange}
            paginationMode="server"
            filterMode="server"
            sortingMode="server"
            onSortChange={handleSortChange}
            externalSortModel={sortModel}
            width="100%"
            maxHeight="100%"
            rowHeight={44}
            loading={zonesLoading}
            onRowClick={handleRowClick}
            onRowDoubleClick={handleRowDoubleClick}
          />
        </Box>
      </Box>

      <WaterTreeEditorDialog
        open={isEditorOpen}
        mode={selectedZone ? 'edit' : 'create'}
        initial={selectedZone ? {
          name: selectedZone.zoneName,
          zoneTypeId: selectedZone.zoneTypeId ?? null,
        } : undefined}
        zoneId={selectedZone?.id}
        onClose={() => setIsEditorOpen(false)}
        onCreate={handleCreate}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
        zoneTypes={zoneTypes}
        copyOptions={copyOptions}
      />
    </Box>
  )
}
